// Item type catalog (§11). Per-type field templates with field types and secret flags.
// All field values are encrypted inside enc_content; only `type` is plaintext (§19).

export type FieldType =
  | "text"
  | "secret"
  | "url"
  | "host"
  | "port"
  | "username"
  | "password"
  | "totp"
  | "otp_recovery_codes"
  | "ssh_private_key"
  | "ssh_public_key"
  | "passphrase"
  | "certificate"
  | "private_key"
  | "psk"
  | "enum"
  | "date"
  | "note"
  | "connection_string";

/** Which group of the editor a field belongs to. "advanced" starts collapsed. */
export type FieldSection = "essential" | "auth" | "advanced";

export interface FieldTemplate {
  key: string;
  label: string;
  type: FieldType;
  secret?: boolean;
  multiline?: boolean;
  options?: string[]; // for enum
  defaultValue?: string;
  // --- editor-only presentation metadata (§11) -------------------------------------------------
  // Never written into enc_content: the editor looks these up by type+key at render time, so the
  // stored item shape and the wire format are unchanged.
  /** Defaults to "essential". */
  section?: FieldSection;
  /** Show this field only while another field in the same item has one of these values. */
  visibleWhen?: { key: string; equals: string[] };
}

export interface TypeTemplate {
  type: string;
  label: string;
  icon: string;
  fields: FieldTemplate[];
}

const f = (key: string, label: string, type: FieldType, extra: Partial<FieldTemplate> = {}): FieldTemplate => ({
  key,
  label,
  type,
  secret:
    type === "secret" ||
    type === "password" ||
    type === "passphrase" ||
    type === "totp" ||
    type === "otp_recovery_codes" ||
    type === "ssh_private_key" ||
    type === "private_key" ||
    type === "psk" ||
    type === "certificate" ||
    type === "connection_string",
  multiline: type === "ssh_private_key" || type === "ssh_public_key" || type === "certificate" || type === "private_key" || type === "note" || type === "otp_recovery_codes",
  ...extra,
});

// The §11 catalog. "Generic" carries no template (free-form fields).
export const CATALOG: TypeTemplate[] = [
  {
    type: "login",
    label: "Login",
    icon: "🔑",
    fields: [
      f("url", "URL", "url"),
      f("username", "Username", "username", { section: "auth" }),
      f("password", "Password", "password", { section: "auth" }),
      f("otp_recovery_codes", "OTP recovery codes", "otp_recovery_codes", { section: "advanced" }),
    ],
  },
  {
    type: "website",
    label: "Website",
    icon: "🌍",
    fields: [
      f("url", "URL", "url"),
      f("username", "Username", "username", { section: "auth" }),
      f("password", "Password", "password", { section: "auth" }),
    ],
  },
  {
    type: "ssh",
    label: "SSH",
    icon: "🖥️",
    fields: [
      f("host", "Host", "host"),
      f("port", "Port", "port", { defaultValue: "22" }),
      f("username", "Username", "username"),
      f("auth_method", "Auth method", "enum", { options: ["password", "key"], defaultValue: "password", section: "auth" }),
      // The key trio and the password are mutually exclusive; hidden ≠ cleared, the values are kept
      // and still saved, so toggling the method back brings them straight back.
      f("password", "Password", "password", { section: "auth", visibleWhen: { key: "auth_method", equals: ["password", ""] } }),
      f("ssh_private_key", "Private key", "ssh_private_key", { section: "auth", visibleWhen: { key: "auth_method", equals: ["key"] } }),
      f("passphrase", "Key passphrase", "passphrase", { section: "auth", visibleWhen: { key: "auth_method", equals: ["key"] } }),
      f("ssh_public_key", "Public key", "ssh_public_key", { section: "auth", visibleWhen: { key: "auth_method", equals: ["key"] } }),
      f("host_key_fingerprint", "Host key fingerprint", "text", { section: "advanced" }),
      f("jump_host", "Jump host", "host", { section: "advanced" }),
      f("escalation", "Escalation (sudo/su)", "secret", { section: "advanced" }),
      // Jump-server outbound connector to route this brokered session through (connector id, or blank
      // = org default / direct). Only used by the clientless web (Lane B) launch.
      f("connector", "Connector", "text", { section: "advanced" }),
    ],
  },
  {
    type: "rdp",
    label: "RDP",
    icon: "🪟",
    fields: [
      f("host", "Host", "host"),
      f("port", "Port", "port", { defaultValue: "3389" }),
      f("domain", "Domain", "text"),
      f("username", "Username", "username", { section: "auth" }),
      f("password", "Password", "password", { section: "auth" }),
      f("gateway_address", "Gateway address", "host", { section: "advanced" }),
      f("gateway_credentials", "Gateway credentials", "enum", {
        options: ["Use connection credentials", "Separate gateway credentials"],
        defaultValue: "Use connection credentials",
        section: "advanced",
      }),
      f("gateway_username", "Gateway username", "username", {
        section: "advanced",
        visibleWhen: { key: "gateway_credentials", equals: ["Separate gateway credentials"] },
      }),
      f("gateway_password", "Gateway password", "password", {
        section: "advanced",
        visibleWhen: { key: "gateway_credentials", equals: ["Separate gateway credentials"] },
      }),
      // Display/redirection options (resolution, colour depth, audio, drive/printer redirection) were
      // removed to keep the form simple: the default embedded client always fits the window, and the
      // native client falls back to sensible defaults (fit-to-window/SmartSizing, 32bpp, clipboard on,
      // no drive/printer redirect) via RdpParams::from_fields when these fields are absent.
      // Embedded = in-app tab (IronRDP, cross-platform, routes through the RD Gateway via MS-TSGU).
      // Native = OS client window (full fidelity). Default is embedded; a connect failure offers a
      // one-click switch to the native client. Per-item override of the global "RDP client" setting.
      f("rdp_client", "RDP client", "enum", {
        options: ["Embedded (in-app)", "Native OS client"],
        defaultValue: "Embedded (in-app)",
        section: "advanced",
      }),
      // Jump-server outbound connector to route this brokered session through (connector id, or blank
      // = org default / direct). Only used by the clientless web (Lane B) launch.
      f("connector", "Connector", "text", { section: "advanced" }),
    ],
  },
  {
    type: "vpn",
    label: "VPN",
    icon: "🛡️",
    fields: [
      f("vpn_type", "VPN type", "enum", { options: ["IPsec", "OpenVPN", "WireGuard", "SSL", "L2TP"] }),
      f("server_address", "Server address", "host"),
      f("username", "Username", "username", { section: "auth" }),
      f("password", "Password", "password", { section: "auth" }),
      f("certificate", "Certificate", "certificate", { section: "auth" }),
      f("private_key", "Private key", "private_key", { section: "auth" }),
      f("passphrase", "Passphrase", "passphrase", { section: "auth" }),
      f("psk", "PSK", "psk", { section: "auth" }),
    ],
  },
  {
    type: "network_device",
    label: "Network device",
    icon: "🌐",
    fields: [
      f("mgmt_address", "Mgmt address", "host"),
      f("mgmt_url", "Mgmt URL", "url"),
      f("username", "Username", "username", { section: "auth" }),
      f("password", "Password", "password", { section: "auth" }),
      f("enable_password", "Enable password", "password", { section: "auth" }),
    ],
  },
  {
    type: "database",
    label: "Database",
    icon: "🗃️",
    fields: [
      f("engine", "Engine", "enum", { options: ["PostgreSQL", "MySQL", "MSSQL", "Oracle", "MongoDB", "Other"] }),
      f("host", "Host", "host"),
      f("port", "Port", "port"),
      f("db_name", "Database", "text"),
      f("username", "Username", "username", { section: "auth" }),
      f("password", "Password", "password", { section: "auth" }),
      f("connection_string", "Connection string", "connection_string", { section: "advanced" }),
    ],
  },
  {
    type: "api_key",
    label: "API key",
    icon: "🔌",
    fields: [
      f("endpoint_url", "Endpoint URL", "url"),
      f("key_id", "Key ID", "text"),
      f("secret", "Secret", "secret", { section: "auth" }),
      f("token", "Token", "secret", { section: "auth" }),
      f("scopes", "Scopes", "text", { section: "advanced" }),
      f("expires_at", "Expires", "date", { section: "advanced" }),
    ],
  },
  {
    type: "certificate",
    label: "Certificate",
    icon: "📜",
    fields: [
      f("certificate", "Certificate", "certificate"),
      f("private_key", "Private key", "private_key", { section: "auth" }),
      f("passphrase", "Passphrase", "passphrase", { section: "auth" }),
      f("ca_chain", "CA chain", "certificate", { section: "advanced" }),
      f("fingerprint", "Fingerprint", "text", { section: "advanced" }),
      f("expires_at", "Expires", "date", { section: "advanced" }),
    ],
  },
  {
    type: "wifi",
    label: "Wi-Fi",
    icon: "📶",
    fields: [
      f("ssid", "SSID", "text"),
      f("security", "Security", "enum", { options: ["WPA2", "WPA3", "WPA", "WEP", "Open"] }),
      f("password", "Password", "password", { section: "auth" }),
    ],
  },
  { type: "secure_note", label: "Secure note", icon: "📝", fields: [f("body", "Body", "note", { secret: true })] },
  { type: "generic", label: "Generic", icon: "📄", fields: [] },
];

export const TYPE_BY_KEY: Record<string, TypeTemplate> = Object.fromEntries(CATALOG.map((t) => [t.type, t]));

export function templateFor(type: string): TypeTemplate {
  return TYPE_BY_KEY[type] ?? TYPE_BY_KEY["generic"];
}

/** The template for one field, scoped to its own item type. Looking a key up across the whole
 *  catalog picks the wrong entry for keys several types share (port, password, connector, …). */
export function fieldTemplate(type: string, key: string): FieldTemplate | undefined {
  return templateFor(type).fields.find((f) => f.key === key);
}

/** Section a field belongs to; anything without metadata (custom/legacy fields) is essential. */
export function sectionOf(type: string, key: string): FieldSection {
  return fieldTemplate(type, key)?.section ?? "essential";
}

/** Is a conditional field currently applicable? Hidden fields keep their value and are still saved. */
export function fieldVisible(type: string, key: string, values: (key: string) => string): boolean {
  const cond = fieldTemplate(type, key)?.visibleWhen;
  return !cond || cond.equals.includes(values(cond.key));
}

export const SECTION_ORDER: FieldSection[] = ["essential", "auth", "advanced"];

/** A representative non-secret value for grid columns (host-like / username). */
export function fieldValue(fields: { key: string; value: string }[], ...keys: string[]): string | undefined {
  for (const k of keys) {
    const found = fields.find((x) => x.key === k && x.value);
    if (found) return found.value;
  }
  return undefined;
}
